import React from "react";

import { ButtonStandard, Text } from "@performyard/py-components";

import ContentModal from "../../../../../components/Modal/Templates/ContentModal";

import type { CalendarErrorType } from "./types";
import { CALENDAR_ERROR_TYPE } from "./types";

import type { CalendarErrorModalState } from "./hooks";

type CalendarErrorModalProps = {
	modalState: CalendarErrorModalState;
	onRetry: () => void;
	onContinue: () => void;
	onClose: () => void;
};

const getCalendarErrorModalContent = (
	errorType: CalendarErrorType
): {
	title: string;
	message: string;
	actions: {
		left: { text: string; color: "primary" | "green" | "red" };
		right: { text: string; color: "primary" | "green" | "red" };
	};
} => {
	switch (errorType) {
		case CALENDAR_ERROR_TYPE.CREATE_FAILED:
			return {
				title: "Failed to Add to Calendar",
				message:
					"Would you like to retry or continue without adding the meeting to your calendar? You are able to add the meeting to your calendar after creating.",
				actions: {
					left: { text: "Retry", color: "primary" },
					right: { text: "Continue Without Adding", color: "red" },
				},
			};

		case CALENDAR_ERROR_TYPE.UPDATE_FAILED:
			return {
				title: "Meeting Update Failed",
				message:
					"Would you like to retry or continue without updating the meeting in PerformYard and on your calendar? You can try again at any time.",
				actions: {
					left: { text: "Cancel", color: "primary" },
					right: { text: "Retry", color: "green" },
				},
			};

		default:
			return {
				title: "Calendar Error",
				message: "An error occurred with calendar integration. Would you like to retry?",
				actions: {
					left: { text: "Cancel", color: "primary" },
					right: { text: "Retry", color: "green" },
				},
			};
	}
};

export const CalendarErrorModal = ({
	modalState,
	onRetry,
	onContinue,
	onClose,
}: CalendarErrorModalProps): React.ReactElement | null => {
	if (modalState.type === "none") {
		return null;
	}

	const { title, message, actions } = getCalendarErrorModalContent(modalState.errorType);

	const getActionHandlers = (): {
		leftButton: { text: string; color: "primary" | "green" | "red"; onClick: () => void };
		rightButton: { text: string; color: "primary" | "green" | "red"; onClick: () => void };
	} => {
		switch (modalState.errorType) {
			case CALENDAR_ERROR_TYPE.CREATE_FAILED:
				return {
					leftButton: {
						text: actions.left.text,
						color: actions.left.color,
						onClick: onRetry,
					},
					rightButton: {
						text: actions.right.text,
						color: actions.right.color,
						onClick: onContinue,
					},
				};

			case CALENDAR_ERROR_TYPE.UPDATE_FAILED:
				return {
					leftButton: {
						text: actions.left.text,
						color: actions.left.color,
						onClick: onClose,
					},
					rightButton: {
						text: actions.right.text,
						color: actions.right.color,
						onClick: onRetry,
					},
				};

			default:
				return {
					leftButton: {
						text: "Cancel",
						color: "primary",
						onClick: onClose,
					},
					rightButton: {
						text: "Retry",
						color: "green",
						onClick: onRetry,
					},
				};
		}
	};

	const actionHandlers = getActionHandlers();

	return (
		<ContentModal
			isOpen={true}
			handleClose={modalState.isRetrying ? () => {} : onClose}
			size="large"
			title={
				<Text size="sub-heading" weight="semi-bold">
					{title}
				</Text>
			}
			buttons={[
				<ButtonStandard
					key="left"
					size="large"
					color={actionHandlers.leftButton.color}
					onClick={actionHandlers.leftButton.onClick}
					disabled={modalState.isRetrying}
				>
					{actionHandlers.leftButton.text}
				</ButtonStandard>,
				<ButtonStandard
					key="right"
					size="large"
					color={actionHandlers.rightButton.color}
					onClick={actionHandlers.rightButton.onClick}
					disabled={modalState.isRetrying}
				>
					{actionHandlers.rightButton.text}
				</ButtonStandard>,
			]}
		>
			<div className="text-center">
				<Text size="paragraph-1" weight="regular">
					{message}
				</Text>
			</div>
		</ContentModal>
	);
};
